import React, { createContext, useContext, useEffect, useState } from 'react';

// 1. Define the shape of our context
type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
}

// 2. Create the context with a default value
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// 3. Create the Provider component
export function ThemeProvider({ children }: { children: React.ReactNode }) {
  // Initialize theme - SSR-safe: check if localStorage exists
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      const savedTheme = localStorage.getItem('theme') as Theme | null;
      return savedTheme || 'dark';
    }
    return 'dark'; // Default for SSR
  });

  // Update the DOM and localStorage when theme changes
  useEffect(() => {
    if (typeof window === 'undefined') return; // Skip on SSR
    
    const root = window.document.documentElement;

    // Remove both classes first
    root.classList.remove('light', 'dark');

    // Add the current theme class
    root.classList.add(theme);

    // Save to localStorage
    localStorage.setItem('theme', theme);
  }, [theme]);

  // Function to toggle between light and dark
  const toggleTheme = () => {
    setThemeState(prevTheme => prevTheme === 'light' ? 'dark' : 'light');
  };

  // Function to set a specific theme
  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
  };

  // Provide the theme state and functions to children
  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

// 4. Create a custom hook for consuming the context
export function useTheme() {
  const context = useContext(ThemeContext);

  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }

  return context;
}
